// «?»-справка модели (особенности + FAQ) для виджета генерации и чата.
//
// Выдача: запись модели → универсальная запись категории (бэк) → хардкод-
// fallback ниже (правило graceful fallbacks: модал никогда не пустой, даже
// если бэк недоступен). Успешные ответы кэшируются на сессию.
import type { ModelCategory } from '../data/models'

const API_BASE = import.meta.env.VITE_API_URL || ''

export interface HelpFeature {
	title: string
	body: string
	sort?: number
}

export interface HelpFaq {
	q: string
	a: string
	sort?: number
}

/** Щит ограничения контента: level 1-3 — жёсткость (полный щит = тема
 *  почти всегда под цензурой модели), title/text — тултип. */
export interface HelpRestriction {
	level: number
	title: string
	text: string
}

export interface ModelHelpData {
	features: HelpFeature[]
	faq: HelpFaq[]
	restrictions: HelpRestriction[]
	source: 'model' | 'category' | 'fallback'
}

// Компактные универсалки на случай недоступного бэка — короче серверного
// сида, но модал остаётся полезным.
export const FALLBACK_HELP: Record<ModelCategory, Pick<ModelHelpData, 'features' | 'faq'>> = {
	text: {
		features: [
			{ title: 'Помнит контекст диалога', body: 'Модель учитывает предыдущие сообщения этого чата. Новую тему лучше начинать новым чатом.' },
			{ title: 'Конкретный запрос — лучший ответ', body: 'Опишите задачу, формат и пример результата — ответ будет точнее.' },
		],
		faq: [
			{ q: 'Почему ответ оборвался?', a: 'Ответ упёрся в лимит длины. Напишите «продолжи» — модель допишет с того же места.' },
			{ q: 'За что списываются токены?', a: 'За объём текста: ваши сообщения и ответ модели.' },
		],
	},
	image: {
		features: [
			{ title: 'Подробный промпт — лучший результат', body: 'Опишите объект, стиль, свет и фон. Чем детальнее описание, тем ближе картинка к задумке.' },
			{ title: 'Формат и качество — в настройках', body: 'Соотношение сторон и качество меняются в панели под полем ввода и влияют на цену.' },
		],
		faq: [
			{ q: 'Почему кнопка «Создать» неактивна?', a: 'Не хватает данных: нужен текст запроса, а в режиме Image to Image — ещё и загруженное фото.' },
			{ q: 'Сколько стоит генерация?', a: 'Точная цена всегда показана на кнопке «Создать».' },
		],
	},
	video: {
		features: [
			{ title: 'Видео генерируется дольше', body: 'Обычно 1–5 минут. Результат появится в «Истории» и «Моих генерациях».' },
			{ title: 'Длительность и разрешение = цена', body: 'Чем длиннее ролик и выше разрешение, тем дороже. Точная цена — на кнопке «Создать».' },
		],
		faq: [
			{ q: 'Генерация не удалась — токены сгорели?', a: 'Нет: при ошибке провайдера списание возвращается на баланс автоматически.' },
			{ q: 'Где мои видео?', a: 'Во вкладке «История» рядом с виджетом и в разделе «Мои генерации».' },
		],
	},
	audio: {
		features: [
			{ title: 'Текст песни или описание стиля', body: 'Можно дать готовый текст песни, а можно описать жанр и настроение — нейросеть сочинит сама.' },
			{ title: 'Трек живёт дальше', body: 'У готового трека есть кабинет: продлить, ремикс, дорожки, WAV.' },
		],
		faq: [
			{ q: 'Сколько хранится аудио?', a: 'Файлы провайдера живут ограниченное время — скачивайте понравившееся сразу.' },
			{ q: 'Получилось два варианта — это норма?', a: 'Да, музыкальные модели обычно отдают две версии за одну генерацию.' },
		],
	},
	presentation: {
		features: [
			{ title: 'Тема → готовая презентация', body: 'Опишите тему и аудиторию — нейросеть соберёт структуру, тексты и оформление.' },
			{ title: 'Объём и стиль — в настройках', body: 'Количество слайдов и стиль выбираются до генерации и влияют на цену.' },
		],
		faq: [
			{ q: 'Можно ли отредактировать результат?', a: 'Да: скачайте файл и правьте в любом редакторе презентаций.' },
			{ q: 'От чего зависит цена?', a: 'Главным образом от количества слайдов. Точная цена — на кнопке.' },
		],
	},
}

const cache = new Map<string, ModelHelpData>()

const bySort = <T extends { sort?: number }>(arr: T[]): T[] =>
	arr.slice().sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))

/** Справка модели с фолбэком. Никогда не кидает — в худшем случае хардкод категории. */
export async function getModelHelp(adapter: string, modelId: string, category: ModelCategory): Promise<ModelHelpData> {
	const key = `${adapter}::${modelId}::${category}`
	const hit = cache.get(key)
	if (hit) return hit
	try {
		const qs = new URLSearchParams({ adapter, model: modelId, category })
		const r = await fetch(`${API_BASE}/api/v1/model-help?${qs.toString()}`)
		const d = await r.json()
		if (d?.ok && d.help && ((d.help.features?.length || 0) + (d.help.faq?.length || 0) + (d.help.restrictions?.length || 0)) > 0) {
			const val: ModelHelpData = {
				features: bySort(d.help.features || []),
				faq: bySort(d.help.faq || []),
				restrictions: d.help.restrictions || [],
				source: d.source === 'model' ? 'model' : 'category',
			}
			cache.set(key, val)
			return val
		}
	} catch {
		// сеть/бэк недоступны — ниже хардкод
	}
	const fb = FALLBACK_HELP[category] || FALLBACK_HELP.image
	// Фолбэк не кэшируем: при следующем открытии попробуем бэк ещё раз.
	return { features: fb.features, faq: fb.faq, restrictions: [], source: 'fallback' }
}


// ── Щиты ограничений для селекта моделей ────────────────────────────
// Один запрос на сессию: селект рисует щиты у каждой строки, per-model
// запросы дали бы N штук на каждое открытие списка. Ошибка сети → пустая
// карта: селект просто без щитов (graceful).
let restrictionsPromise: Promise<Map<string, HelpRestriction[]>> | null = null

export function getAllRestrictions(): Promise<Map<string, HelpRestriction[]>> {
	if (!restrictionsPromise) {
		restrictionsPromise = (async () => {
			const map = new Map<string, HelpRestriction[]>()
			try {
				const r = await fetch(`${API_BASE}/api/v1/model-help/restrictions`)
				const d = await r.json()
				for (const it of d?.items || []) {
					if (Array.isArray(it.restrictions) && it.restrictions.length) {
						map.set(`${it.adapter}::${it.model_id}`, it.restrictions)
					}
				}
			} catch {
				// сеть/старый бэк — щиты не показываем
			}
			return map
		})()
	}
	return restrictionsPromise
}
